import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from 'components/Shared/Button'
import EmptyState from 'components/Shared/EmptyState'
import Loading from 'components/Shared/Loading'

import { getCurrentUser, getCurrentUserToken } from 'ducks/users/index.ts'
import {
  getCurrentOrganization,
  fetchOrganizations,
  fetchOrganization,
} from 'ducks/organizations'

import { hasBusinessPlan, isCurrentPlanType } from 'utils/billing.ts'

import Members from './Members'
import TeamInviteModal from './TeamInviteModal'

import './Team.css'

const TeamSettings = () => {
  const dispatch = useDispatch()

  // Get selectors first
  const org = useSelector(getCurrentOrganization)
  const currentUser = useSelector(getCurrentUser)
  const currentUserToken = useSelector(getCurrentUserToken)

  const [isInviteModalOpen, setInviteModalOpen] = useState(false)

  const refreshData = useCallback(
    id => {
      // Only fetch an individual organization when we actually have an id
      if (id && currentUserToken) {
        dispatch(fetchOrganization(id))
      }

      // Always refresh the organizations collection so derived selectors stay up-to-date (only if we have a valid token)
      if (currentUserToken) {
        dispatch(fetchOrganizations())
      }
    },
    [dispatch, currentUserToken]
  )

  const currentUserIsAdmin = currentUser?.admin

  const seatLimit = useMemo(() => {
    if (hasBusinessPlan(org?.planType)) {
      return 'unlimited'
    }

    return org?.addons?.seats?.limit
  }, [org?.addons?.seats?.limit, org?.planType])

  useEffect(() => {
    if (org?.id) {
      // We have an organization, make sure its details are fresh
      refreshData(org.id)
    } else {
      // No organization yet – pull the list so we can pick one (only if we have a valid token)
      if (currentUserToken) {
        dispatch(fetchOrganizations())
      }
    }
  }, [org?.id, currentUserToken, refreshData, dispatch])

  const currentUserId = useMemo(
    () => (currentUserIsAdmin ? currentUser.id : org?.OrganizationUser?.UserId),
    [currentUserIsAdmin, currentUser?.id, org?.OrganizationUser?.UserId]
  )

  const currentPlanType = useMemo(
    () => isCurrentPlanType(org?.planType),
    [org?.planType]
  )

  const showSeatUsage = useMemo(() => {
    return currentUserIsAdmin || currentPlanType || org?.planType === 'free'
  }, [org?.Users, org?.planType, currentUserIsAdmin, currentPlanType])

  const currentUsers = useMemo(
    () => org?.addons?.seats?.count,
    [org?.addons?.seats?.count]
  )

  const userIsAdmin = useMemo(() => {
    // if the User is an admin (this is an internal Adalo admin)
    if (currentUserIsAdmin) {
      return true
    }

    // if the OrganizationUser is an admin (this is a maker who is an admin for their org)
    const members = org?.Users
    const user = members?.find(u => u.id === currentUserId)

    return user?.OrganizationUser.isAdmin
  }, [currentUserIsAdmin, org?.Users?.length])

  if (!currentUserId) {
    return null
  }

  if (!org) {
    return (
      <EmptyState>
        <Loading />
      </EmptyState>
    )
  }

  return (
    <div className="team-settings-sub">
      <div className="team-settings-header">
        <div className="settings-header-wrapper">
          <h3 className="settings-header">Team Members</h3>
          {showSeatUsage && (
            <SeatUsage currentUsers={currentUsers} seatLimit={seatLimit} />
          )}
        </div>
        {userIsAdmin ? (
          <Button
            small
            data-adalo-id="invite-new-editor-team-members"
            onClick={() => setInviteModalOpen(true)}
          >
            Invite
          </Button>
        ) : null}
      </div>
      <Members
        organization={org}
        currentUserId={currentUserId}
        userIsAdmin={userIsAdmin}
        refreshData={refreshData}
        seatLimit={seatLimit}
        currentUsers={currentUsers}
      />
      {isInviteModalOpen && (
        <TeamInviteModal
          organization={org}
          currentUser={currentUser}
          onClose={() => setInviteModalOpen(false)}
          onSuccess={() => {
            setInviteModalOpen(false)
            refreshData(org.id)
          }}
        />
      )}
    </div>
  )
}

const SeatUsage = ({ currentUsers, seatLimit }) => {
  if (typeof seatLimit !== 'number' && seatLimit !== 'unlimited') {
    return `(${currentUsers})`
  }

  const usage = `(${currentUsers} out of ${seatLimit})`

  return <p className="editor-limit-subheading">{usage}</p>
}

export default TeamSettings
